import type { ShopProduct, ShopProductVariant } from "@mall-mart/shared";
import { soldLine } from "@mall-mart/shared";
import { resizeShopifyImage } from "../world/imageUtils.js";

/**
 * Floating DOM panel that opens when a product is clicked. Lets the user
 * pick a variant (e.g. color + size) before adding to cart. MVP uses DOM
 * (simple, cross-platform, no 3D text glyphs to wrestle with). A Babylon
 * GUI port for in-VR usage is a later task.
 */
export class ProductPanel {
  private el: HTMLElement;
  private onAdd: (product: ShopProduct, variant: ShopProductVariant) => void;
  private product: ShopProduct | undefined;
  private unitsSold: Record<string, number> = {};
  private selectedOptions: Record<string, string> = {};

  constructor(
    elementId: string,
    onAdd: (product: ShopProduct, variant: ShopProductVariant) => void
  ) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`ProductPanel element #${elementId} not found`);
    }
    this.el = found;
    this.onAdd = onAdd;

    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && !this.el.hidden) {
        this.close();
      }
    });
  }

  /** @param unitsSold this shop's handle -> all-time units map, for the sold
   *  line under the price. Empty when the stats section was unreachable. */
  open(product: ShopProduct, unitsSold: Record<string, number>): void {
    this.product = product;
    this.unitsSold = unitsSold;
    this.selectedOptions = {};
    // Pre-select the first option value for each optionType so a variant is
    // always resolvable.
    const optionTypes: string[] = product.optionTypes ?? [];
    for (const type of optionTypes) {
      const first: string | undefined = product.variants
        .map((v) => v.options?.[type])
        .find((v): v is string => v !== undefined);
      if (first !== undefined) {
        this.selectedOptions[type] = first;
      }
    }
    this.render();
    this.el.hidden = false;
  }

  close(): void {
    this.el.hidden = true;
    this.product = undefined;
  }

  isOpen(): boolean {
    return !this.el.hidden;
  }

  private render(): void {
    const p: ShopProduct | undefined = this.product;
    if (p === undefined) {
      return;
    }

    // Price follows the selected variant (variants can differ in price);
    // fall back to the range only while no variant resolves yet.
    const resolved: ShopProductVariant | undefined = this.resolveVariant();
    const priceText: string =
      resolved !== undefined
        ? `${resolved.price} ${resolved.currency}`
        : p.priceRange.min === p.priceRange.max
          ? `${p.priceRange.min} ${p.priceRange.currency}`
          : `${p.priceRange.min} to ${p.priceRange.max} ${p.priceRange.currency}`;

    this.el.replaceChildren();

    const closeBtn: HTMLButtonElement = document.createElement("button");
    closeBtn.className = "pp-close";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => this.close());
    this.el.appendChild(closeBtn);

    const h2: HTMLHeadingElement = document.createElement("h2");
    h2.textContent = p.title;
    this.el.appendChild(h2);

    const priceDiv: HTMLDivElement = document.createElement("div");
    priceDiv.className = "pp-price";
    priceDiv.textContent = priceText;
    this.el.appendChild(priceDiv);

    const soldDiv: HTMLDivElement = document.createElement("div");
    soldDiv.className = "pp-sold";
    soldDiv.textContent = soldLine(p, this.unitsSold);
    this.el.appendChild(soldDiv);

    const gallery: HTMLElement | undefined = this.buildGallery(p, resolved);
    if (gallery !== undefined) {
      this.el.appendChild(gallery);
    }

    const optionTypes: string[] = p.optionTypes ?? [];
    const optionsDiv: HTMLDivElement = document.createElement("div");
    optionsDiv.className = "pp-options";
    for (const type of optionTypes) {
      const group: HTMLDivElement = document.createElement("div");
      group.className = "pp-option-group";
      const label: HTMLLabelElement = document.createElement("label");
      label.textContent = type;
      group.appendChild(label);

      const chips: HTMLDivElement = document.createElement("div");
      chips.className = "pp-chips";
      const values: string[] = Array.from(
        new Set(
          p.variants
            .map((v) => v.options?.[type])
            .filter((v): v is string => v !== undefined)
        )
      );
      for (const value of values) {
        const chip: HTMLButtonElement = document.createElement("button");
        chip.className = "pp-chip";
        chip.textContent = value;
        if (this.selectedOptions[type] === value) {
          chip.classList.add("active");
        }
        chip.addEventListener("click", () => {
          this.selectedOptions[type] = value;
          this.render();
        });
        chips.appendChild(chip);
      }
      group.appendChild(chips);
      optionsDiv.appendChild(group);
    }
    this.el.appendChild(optionsDiv);

    const addBtn: HTMLButtonElement = document.createElement("button");
    addBtn.className = "pp-add";
    if (resolved === undefined) {
      addBtn.textContent = "Select options";
      addBtn.disabled = true;
    } else if (resolved.availability !== "in_stock") {
      // The variant is out of stock, so it must never look purchasable.
      addBtn.textContent = "Sold out";
      addBtn.disabled = true;
    } else {
      addBtn.textContent = `Add to cart — ${resolved.price} ${resolved.currency}`;
      addBtn.addEventListener("click", () => {
        this.onAdd(p, resolved);
        this.close();
      });
    }
    this.el.appendChild(addBtn);
  }

  /**
   * Build a horizontally-scrollable image gallery. Products usually carry
   * several loose photos in `images[]` that are not tied to a specific
   * variant, so we show the full set and let the user swipe/scroll or use the
   * arrows. When the selected variant has its own distinct photo we surface it
   * as the first slide so the panel reflects the current choice.
   */
  private buildGallery(
    p: ShopProduct,
    resolved: ShopProductVariant | undefined
  ): HTMLElement | undefined {
    const urls: string[] = p.images.map((img) => img.url);
    const variantUrl: string | undefined =
      resolved !== undefined && resolved.imageUrl.length > 0
        ? resolved.imageUrl
        : undefined;
    if (variantUrl !== undefined && !urls.includes(variantUrl)) {
      urls.unshift(variantUrl);
    } else if (variantUrl !== undefined) {
      // Move the variant's photo to the front without duplicating it.
      urls.splice(urls.indexOf(variantUrl), 1);
      urls.unshift(variantUrl);
    }
    if (urls.length === 0) {
      return undefined;
    }

    const gallery: HTMLDivElement = document.createElement("div");
    gallery.className = "pp-gallery";

    const track: HTMLDivElement = document.createElement("div");
    track.className = "pp-gallery-track";
    for (const url of urls) {
      const img: HTMLImageElement = document.createElement("img");
      img.className = "pp-gallery-slide";
      img.src = resizeShopifyImage(url, 800); // panel is <=520px wide
      img.alt = resolved?.title ?? p.title;
      img.loading = "lazy";
      track.appendChild(img);
    }
    gallery.appendChild(track);

    // Arrows only make sense with more than one image.
    if (urls.length > 1) {
      const prev: HTMLButtonElement = document.createElement("button");
      prev.className = "pp-gallery-arrow pp-gallery-prev";
      prev.setAttribute("aria-label", "Previous image");
      prev.textContent = "‹";
      prev.addEventListener("click", () => this.scrollGallery(track, -1));
      gallery.appendChild(prev);

      const next: HTMLButtonElement = document.createElement("button");
      next.className = "pp-gallery-arrow pp-gallery-next";
      next.setAttribute("aria-label", "Next image");
      next.textContent = "›";
      next.addEventListener("click", () => this.scrollGallery(track, 1));
      gallery.appendChild(next);
    }

    return gallery;
  }

  private scrollGallery(track: HTMLElement, direction: number): void {
    if (typeof track.scrollBy !== "function") {
      return; // jsdom / unsupported environments
    }
    const step: number = track.clientWidth > 0 ? track.clientWidth : 300;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  }

  private resolveVariant(): ShopProductVariant | undefined {
    if (this.product === undefined) {
      return undefined;
    }
    for (const v of this.product.variants) {
      const opts: Record<string, string> | undefined = v.options;
      if (opts === undefined) {
        continue;
      }
      let match: boolean = true;
      for (const [k, want] of Object.entries(this.selectedOptions)) {
        if (opts[k] !== want) {
          match = false;
          break;
        }
      }
      if (match) {
        return v;
      }
    }
    // Fallback for single-variant products with no options
    if (this.product.variants.length === 1) {
      return this.product.variants[0];
    }
    return undefined;
  }
}
